/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

/**
 * usePage Hook
 *
 * Bu hook, liste sayfalarındaki (Örn: Ürünler, Kategoriler) tüm temel işlevleri tek bir merkezden yönetir.
 * 
 * Özellikler:
 * - API'den veri çekme (Fetch Data)
 * - Yükleme ve Hata durumları (Loading & Error States)
 * - Sayfalama (Pagination)
 * - Sıralama (Sorting)
 * - Filtreleme ve Arama (Filtering & Search)
 * - CRUD İşlemleri (Ekle, Güncelle, Sil)
 * - Modal Yönetimi (Create/Edit Modal)
 * 
 * Kullanım: const page = usePage<Type>({ endpoint: '/api/items' });
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import {
    COMPANY_SCOPE_HEADER,
    pathRequiresSelectedCompanyOnRequest,
    resolveExplicitCompanyScopeIdForRequest,
} from '../config/company-scope.config';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '../utils/language.utils';
import { getValidationMessage } from '../utils/validation.translations';
import { getFirstApiValidationErrorMessage } from '../utils/error.utils';
import { createScopedMemoryCache } from '../utils/scoped-memory-cache.utils';
import { getQubeProFeedbackText, showQubeProDeleteConfirmDialog } from '../utils/ui-feedback.utils';
import { DEFAULT_PAGE_SIZE, useUserPageSizePreference } from './useUserPageSizePreference';

type RequestParams = Record<string, string | number | boolean | null | undefined>;
type ExplicitCompanyScope = string | number | bigint | null | undefined;

interface CompanyScopedMutationOptions {
    /** Hedef kaydın iş firması; global aktif kapsamı değiştirmeden yalnız bu isteğe bağlanır. */
    companyScopeId?: ExplicitCompanyScope;
}

const PAGE_DATA_CACHE_TTL_MS = 2 * 60 * 1000;
const PAGE_DATA_CACHE_MAX_ENTRIES = 80;
const pageDataCache = createScopedMemoryCache<unknown[]>({
    ttlMs: PAGE_DATA_CACHE_TTL_MS,
    maxEntries: PAGE_DATA_CACHE_MAX_ENTRIES,
});

function readPageDataCache<T>(key: string): T[] | null {
    return pageDataCache.read(key) as T[] | null;
}

function writePageDataCache<T>(key: string, rows: T[]): void {
    pageDataCache.write(key, [...rows]);
}

interface PageOptions<T> {
    endpoint: string;
    /**
     * Liste verisini çekmek için ayrı endpoint.
     * - Admin API'larda liste endpoint'i genelde query paramlı olabilir (örn: `?page=1&size=1000`)
     * - CRUD endpoint'i temiz kalmalı (PUT/POST/DELETE için)
     * Varsayılan: endpoint
     */
    listEndpoint?: string;
    /** Server paginated bir yönetim listesindeki tüm sayfaları kontrollü olarak birleştirir. */
    loadAllServerPages?: boolean;
    initialPageSize?: number;
    initialSortColumn?: keyof T | string;
    initialSortDirection?: 'asc' | 'desc';
    searchFields?: (keyof T)[]; // Hangi alanlarda arama yapılacak?
    language?: SupportedLanguage;
    additionalParams?: RequestParams;
    /** false ise veri çekilmez (örn. firma seçilmeden ürün listesi) */
    enabled?: boolean;
    /**
     * Create (POST) URL'ini dinamik üretmek için.
     * Örn: companyId gibi query param gereken endpoint'ler.
     * Varsayılan: endpoint
     */
    getCreateUrl?: (payload: Partial<T>) => string;
    /**
     * Update/Delete (PUT/PATCH/DELETE) URL'ini dinamik üretmek için.
     * Örn: `/api/organizations/{id}?companyId=...`
     * Varsayılan: `${endpoint}/${id}`
     */
    getItemUrl?: (id: string | number, context?: T | Partial<T> | null) => string;
    /**
     * Update için HTTP method seçimi.
     * - Bazı endpoint'ler `PATCH`, bazıları `PUT` bekler.
     * Varsayılan: 'put'
     */
    updateMethod?: 'put' | 'patch';
}

export function usePage<T extends { id: string | number }>(options: PageOptions<T>) {
    const {
        endpoint,
        listEndpoint,
        loadAllServerPages = false,
        initialPageSize = DEFAULT_PAGE_SIZE,
        initialSortColumn = 'createdAt',
        initialSortDirection = 'desc',
        searchFields = ['name' as keyof T], // Varsayılan olarak 'name' alanında ara
        language = DEFAULT_LANGUAGE,
        additionalParams = {},
        enabled = true,
        getCreateUrl: getCreateUrlOption,
        getItemUrl: getItemUrlOption,
        updateMethod = 'put'
    } = options;

    /**
     * Sayfalar `searchFields: ['name', ...]` dizisini render sırasında yeniden
     * oluşturabilir. İçerik değişmediği halde bu referansı useMemo bağımlılığına
     * almak, `page.data` kimliğini değiştirerek tüketici effect'lerini tekrar
     * çalıştırır. Primitive imza + kararlı dizi bu zinciri keser.
     */
    const searchFieldsKey = JSON.stringify(searchFields.map((field) => String(field)));
    const stableSearchFields = useMemo(
        () => JSON.parse(searchFieldsKey) as (keyof T)[],
        [searchFieldsKey],
    );

    /** Dil bootstrap/me sırasında birkaç kez değişebilir; loadData kimliğine bağlanmasın (liste GET fırtınası). */
    const languageRef = useRef<SupportedLanguage>(language);
    languageRef.current = language;

    const { isInitialized, isAuthenticated, user } = useAuth();
    const companyContext = useCompany() as ReturnType<typeof useCompany> & {
        hasHydratedCompanies?: boolean;
        companiesHydrated?: boolean;
    };
    const {
      selectedCompanyId,
      selectedCompany,
      isLoading: companiesLoading,
    } = companyContext;
    const hasHydratedCompanies =
        companyContext.hasHydratedCompanies ??
        companyContext.companiesHydrated ??
        false;
    const listUrlForReadiness = listEndpoint || endpoint;
    const requiresCompanyScope = pathRequiresSelectedCompanyOnRequest(listUrlForReadiness);
    const companyScopeReady =
        !requiresCompanyScope ||
        (
            !companiesLoading &&
            hasHydratedCompanies &&
            Boolean(selectedCompany?.id) &&
            String(selectedCompany?.id) === String(selectedCompanyId)
        );
    const additionalParamsKey = JSON.stringify(additionalParams);
    const requestConfig = useMemo(
        () => ({ params: additionalParams }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [additionalParamsKey],
    );
    const resolveMutationRequestConfig = useCallback((companyScopeId?: ExplicitCompanyScope) => {
        if (companyScopeId === undefined || companyScopeId === null || String(companyScopeId).trim() === '') {
            return requestConfig;
        }

        const normalizedScopeId = resolveExplicitCompanyScopeIdForRequest(companyScopeId);
        if (!normalizedScopeId) {
            throw new Error('Geçersiz firma kapsamı.');
        }

        return {
            ...requestConfig,
            headers: {
                [COMPANY_SCOPE_HEADER]: normalizedScopeId,
            },
        };
    }, [requestConfig]);
    // AuthContext normalizes Core customerId to tenantId. Include auth/permission
    // state so cached rows cannot survive an identity boundary with reused IDs.
    const userCacheIdentity = JSON.stringify([
        Boolean(isInitialized && isAuthenticated),
        String(user?.tenantId ?? (user as any)?.customerId ?? ''),
        String(user?.id ?? user?.email ?? ''),
        user?.permissions ?? [],
    ]);
    const pageDataCacheKey = useMemo(
        () => JSON.stringify([
            userCacheIdentity,
            String(selectedCompanyId ?? ''),
            listEndpoint || endpoint,
            additionalParamsKey,
        ]),
        [additionalParamsKey, endpoint, listEndpoint, selectedCompanyId, userCacheIdentity],
    );

    // A unique generation also fences A -> B -> A identity transitions.
    // Query/filter changes do not discard an editor in the same business scope.
    const mutationScopeKey = JSON.stringify([userCacheIdentity, String(selectedCompanyId ?? ''), endpoint]);
    const mutationScopeRef = useRef({ key: mutationScopeKey, pending: new Set<symbol>(), formSaving: false });
    if (mutationScopeRef.current.key !== mutationScopeKey) {
        mutationScopeRef.current = { key: mutationScopeKey, pending: new Set<symbol>(), formSaving: false };
    }
    const mutationScope = mutationScopeRef.current;
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    const isCurrentScope = useCallback(() => mountedRef.current && mutationScopeRef.current === mutationScope, [mutationScope]);
    const activeLoadKeyRef = useRef(pageDataCacheKey);
    activeLoadKeyRef.current = pageDataCacheKey;

    // Veri Durumu (Data State)
    const initialCacheRef = useRef<{ key: string; rows: T[]; hit: boolean } | null>(null);
    if (initialCacheRef.current === null) {
        const cachedRows = readPageDataCache<T>(pageDataCacheKey);
        initialCacheRef.current = {
            key: pageDataCacheKey,
            rows: cachedRows ?? [],
            hit: cachedRows !== null,
        };
    }
    const [data, setData] = useState<T[]>(() => initialCacheRef.current?.rows ?? []);
    const dataCacheKeyRef = useRef(initialCacheRef.current?.key ?? pageDataCacheKey);
    const loadRequestRef = useRef(0);
    const [loading, setLoading] = useState(() => !(initialCacheRef.current?.hit ?? false));
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Sayfalama Durumu (Pagination State)
    const [currentPage, setCurrentPage] = useState(1);
    const {
        pageSize,
        setPageSize: setPreferredPageSize,
        loading: pageSizePreferenceLoading,
    } = useUserPageSizePreference(initialPageSize);

    // Sıralama Durumu (Sort State)
    const [sortColumn, setSortColumn] = useState<keyof T | string>(initialSortColumn);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);
    const [isSortManuallyChanged, setIsSortManuallyChanged] = useState(false);

    // Filtreleme Durumu (Filter State)
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');
    const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

    // Modal & Form Durumu
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState<T | null>(null);
    /** Salt okunur detay modu (İncele); kayıt oluşturma/düzenleme footer’ı gizlenir */
    const [modalViewOnly, setModalViewOnly] = useState(false);
    // Create modal açarken formu prefill etmek için (Copy gibi)
    const [prefillItem, setPrefillItem] = useState<Partial<T> | null>(null);
    const [saving, setSaving] = useState(false);
    const [formScope, setFormScope] = useState(mutationScope);
    useEffect(() => {
        setFormScope(mutationScope);
        setShowModal(false);
        setEditingItem(null);
        setPrefillItem(null);
        setModalViewOnly(false);
        setSaving(false);
        setError(null);
        setSuccess(null);
    }, [mutationScope]);
    const beginMutation = useCallback(() => {
        const operation = Symbol('mutation');
        mutationScope.pending.add(operation);
        setSaving(true);
        setError(null);
        setSuccess(null);
        return operation;
    }, [mutationScope]);
    const finishMutation = useCallback((operation: symbol) => {
        mutationScope.pending.delete(operation);
        if (isCurrentScope()) setSaving(mutationScope.pending.size > 0);
    }, [isCurrentScope, mutationScope]);

    // --- EYLEMLER (ACTIONS) ---

    const resolveCreateUrl = useCallback((payload: Partial<T>) => {
        try {
            return getCreateUrlOption ? getCreateUrlOption(payload) : endpoint;
        } catch {
            return endpoint;
        }
    }, [endpoint, getCreateUrlOption]);

    const resolveItemUrl = useCallback((id: string | number, context?: T | Partial<T> | null) => {
        try {
            return getItemUrlOption ? getItemUrlOption(id, context ?? null) : `${endpoint}/${id}`;
        } catch {
            return `${endpoint}/${id}`;
        }
    }, [endpoint, getItemUrlOption]);

    // 1. Veri Yükle
    const loadData = useCallback(async () => {
        if (!isCurrentScope() || activeLoadKeyRef.current !== pageDataCacheKey) return;
        const requestId = ++loadRequestRef.current;
        const isCurrentLoad = () => isCurrentScope() && activeLoadKeyRef.current === pageDataCacheKey && requestId === loadRequestRef.current;
        const requestCacheKey = pageDataCacheKey;
        if (!isInitialized || !isAuthenticated) return;
        if (!enabled) {
            if (isCurrentLoad()) {
                dataCacheKeyRef.current = requestCacheKey;
                setLoading(false);
                setError(null);
                setData([]);
            }
            return;
        }
        if (!companyScopeReady) return;

        const cachedRows = readPageDataCache<T>(requestCacheKey);
        dataCacheKeyRef.current = requestCacheKey;
        if (cachedRows) {
            setData(cachedRows);
            setLoading(false);
        } else {
            setData([]);
            setLoading(true);
        }

        try {
            setError(null);

            const listUrl = listEndpoint || endpoint;
            const res = await axios.get(listUrl, requestConfig);
            if (import.meta.env.DEV) {
                // list response debug log intentionally disabled
            }

            // API yanıt formatı esnekliği:
            const responseBody = res.data;
            let rawData = responseBody?.data || responseBody;
            const paginationEnvelope = rawData && !Array.isArray(rawData) ? rawData : null;

            // Eğer data bir array değilse ama içinde 'data' adlı bir array varsa (Pagination wrapper)
            if (rawData && !Array.isArray(rawData) && Array.isArray(rawData.data)) {
                rawData = rawData.data;
            }

            if (!isCurrentLoad()) return;
            let nextRows = Array.isArray(rawData) ? rawData : [];
            const serverTotal = Number(paginationEnvelope?.total);
            const serverSize = Number(paginationEnvelope?.size) || nextRows.length;
            if (loadAllServerPages && Number.isFinite(serverTotal) && serverTotal > nextRows.length && serverSize > 0) {
                const totalPages = Math.ceil(serverTotal / serverSize);
                if (totalPages > 100) throw new Error('Liste güvenli sayfa birleştirme sınırını aşıyor.');
                const [pathPart, queryPart = ''] = listUrl.split('?');
                for (let serverPage = 2; serverPage <= totalPages; serverPage += 1) {
                    const params = new URLSearchParams(queryPart);
                    params.set('page', String(serverPage));
                    params.set('size', String(serverSize));
                    const pageResponse = await axios.get(`${pathPart}?${params.toString()}`, requestConfig);
                    if (!isCurrentLoad()) return;
                    const pageEnvelope = pageResponse.data?.data || pageResponse.data;
                    const pageRows = Array.isArray(pageEnvelope)
                        ? pageEnvelope
                        : Array.isArray(pageEnvelope?.data)
                            ? pageEnvelope.data
                            : [];
                    nextRows = nextRows.concat(pageRows);
                }
            }
            if (!isCurrentLoad()) return;
            writePageDataCache(requestCacheKey, nextRows);
            dataCacheKeyRef.current = requestCacheKey;
            setData(nextRows);
        } catch (err: any) {
            if (!isCurrentLoad()) return;
            const listUrl = listEndpoint || endpoint;
            if (import.meta.env.DEV) {
                console.error(`${listUrl} adresinden veri yüklenirken hata oluştu:`, err);
            }
            setError(
                err.response?.data?.message ||
                    getValidationMessage('failedToLoad', languageRef.current),
            );
            if (!cachedRows) setData([]);
        } finally {
            if (isCurrentLoad()) setLoading(false);
        }
    }, [endpoint, listEndpoint, loadAllServerPages, isInitialized, isAuthenticated, enabled, companyScopeReady, selectedCompanyId, requestConfig, pageDataCacheKey, isCurrentScope]);

    // Page size merkezi kullanıcı tercih hook'u üzerinden yönetilir; usePage yalnızca sayfayı başa alır.
    const setPageSize = useCallback((nextPageSize: number) => {
        setCurrentPage(1);
        void setPreferredPageSize(nextPageSize);
    }, [setPreferredPageSize]);

    // İlk Yükleme
    useEffect(() => {
        loadData();
    }, [loadData]);

    // 2. Yenileme Yardımcısı
    const refresh = useCallback(() => {
        loadData();
    }, [loadData]);

    // 3. Ekle / Güncelle
    const saveItem = useCallback(async (
        formData: Partial<T>,
        options?: CompanyScopedMutationOptions,
    ) => {
        if (!isCurrentScope() || !isInitialized || !isAuthenticated || mutationScope.formSaving) return false;
        mutationScope.formSaving = true;
        const operation = beginMutation();

        try {
            const mutationRequestConfig = resolveMutationRequestConfig(options?.companyScopeId);
            if (editingItem) {
                // Güncelleme
                const url = resolveItemUrl(editingItem.id, editingItem);
                if (updateMethod === 'patch') {
                    await axios.patch(url, formData, mutationRequestConfig);
                } else {
                    await axios.put(url, formData, mutationRequestConfig);
                }
                if (!isCurrentScope()) return false;
                setSuccess(getValidationMessage('operationSuccess', language));
            } else {
                // Oluşturma
                const url = resolveCreateUrl(formData);
                await axios.post(url, formData, mutationRequestConfig);
                if (!isCurrentScope()) return false;
                setSuccess(getValidationMessage('operationSuccess', language));
            }

            await loadData();
            return isCurrentScope();
        } catch (err: any) {
            if (!isCurrentScope()) return false;
            const data = err?.response?.data;
            const apiError =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                null;

            const validationFirst = getFirstApiValidationErrorMessage(data?.errors);

            setError(validationFirst || apiError || getValidationMessage('operationFailed', language));
            return false;
        } finally {
            mutationScope.formSaving = false;
            finishMutation(operation);
        }
    }, [editingItem, loadData, language, resolveCreateUrl, resolveItemUrl, resolveMutationRequestConfig, updateMethod, isCurrentScope, isInitialized, isAuthenticated, mutationScope, beginMutation, finishMutation]);

    // 3b. ID ile güncelle (inline update için - örn. aktif/pasif toggle)
    const updateItem = useCallback(async (
        id: string | number,
        updateData: Partial<T>,
        options?: CompanyScopedMutationOptions & {
            showSuccess?: boolean;
            successMessage?: string;
            refresh?: boolean;
            context?: T | Partial<T> | null;
        }
    ) => {
        if (!isCurrentScope() || !isInitialized || !isAuthenticated) return false;
        const operation = beginMutation();

        try {
            const url = resolveItemUrl(id, options?.context ?? null);
            const mutationRequestConfig = resolveMutationRequestConfig(options?.companyScopeId);
            if (updateMethod === 'patch') {
                await axios.patch(url, updateData, mutationRequestConfig);
            } else {
                await axios.put(url, updateData, mutationRequestConfig);
            }
            if (!isCurrentScope()) return false;
            if (options?.showSuccess !== false) {
                setSuccess(options?.successMessage || getValidationMessage('operationSuccess', language));
            }

            if (options?.refresh !== false) {
                await loadData();
            } else {
                setData(prev => {
                    if (!isCurrentScope() || activeLoadKeyRef.current !== pageDataCacheKey) return prev;
                    const nextRows = prev.map(item =>
                        item.id === id
                            ? ({ ...(item as any), ...(updateData as any) } as T)
                            : item
                    );
                    writePageDataCache(pageDataCacheKey, nextRows);
                    return nextRows;
                });
            }
            return isCurrentScope();
        } catch (err: any) {
            if (!isCurrentScope()) return false;
            const data = err?.response?.data;
            const apiError =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                null;
            setError(apiError || getValidationMessage('operationFailed', language));
            return false;
        } finally {
            finishMutation(operation);
        }
    }, [loadData, language, pageDataCacheKey, resolveItemUrl, resolveMutationRequestConfig, updateMethod, isCurrentScope, isInitialized, isAuthenticated, mutationScope, beginMutation, finishMutation]);

    // 4. Sil
    const deleteItem = useCallback(async (
        id: string | number,
        options?: CompanyScopedMutationOptions & {
            confirm?: boolean;
            confirmMessage?: string;
            confirmTitle?: string;
            confirmLabel?: string;
            cancelLabel?: string;
            context?: T | Partial<T> | null;
        }
    ) => {
        if (!isCurrentScope() || !isInitialized || !isAuthenticated) return false;
        const shouldConfirm = options?.confirm ?? true;
        const confirmMsg =
            options?.confirmMessage || getQubeProFeedbackText('deleteGenericMessage', language);

        if (shouldConfirm) {
            const confirmed = await showQubeProDeleteConfirmDialog({
                language,
                title: options?.confirmTitle,
                message: confirmMsg,
                confirmLabel: options?.confirmLabel,
                cancelLabel: options?.cancelLabel,
            });
            if (!confirmed || !isCurrentScope()) return false;
        }

        const operation = beginMutation();
        try {
            const url = resolveItemUrl(id, options?.context ?? null);
            const mutationRequestConfig = resolveMutationRequestConfig(options?.companyScopeId);
            await axios.delete(url, mutationRequestConfig);
            if (!isCurrentScope()) return false;
            setSuccess(getValidationMessage('deletedSuccessfully', language));
            await loadData();
            return isCurrentScope();
        } catch (err: any) {
            if (!isCurrentScope()) return false;
            const data = err?.response?.data;
            const apiError =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                null;
            setError(apiError || getValidationMessage('operationFailed', language));
            return false;
        } finally {
            finishMutation(operation);
        }
    }, [language, loadData, resolveItemUrl, resolveMutationRequestConfig, isCurrentScope, isInitialized, isAuthenticated, mutationScope, beginMutation, finishMutation]);

    // 5. Modal Yardımcıları
    const openModal = useCallback((item?: Partial<T>, options?: { asCreate?: boolean; viewOnly?: boolean }) => {
        if (!isCurrentScope() || mutationScope.pending.size > 0) return;
        const asCreate = options?.asCreate ?? false;
        const viewOnly = options?.viewOnly ?? false;
        if (asCreate) {
            setEditingItem(null);
            setPrefillItem(item || null);
            setModalViewOnly(false);
        } else {
            setPrefillItem(null);
            setEditingItem((item as T) || null);
            setModalViewOnly(Boolean(item && viewOnly));
        }
        setShowModal(true);
        setError(null);
        setSuccess(null);
    }, [isCurrentScope, mutationScope]);

    const closeModal = useCallback(() => {
        if (!isCurrentScope() || mutationScope.pending.size > 0) return;
        setShowModal(false);
        setEditingItem(null);
        setPrefillItem(null);
        setModalViewOnly(false);
        setError(null);
        setSuccess(null);
    }, [isCurrentScope, mutationScope]);

    // 6. Sıralama Yardımcısı
    const handleSort = useCallback((column: keyof T | string) => {
        setIsSortManuallyChanged(true);
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    }, [sortColumn]);

    // 7. Filtreleme Yardımcıları
    const handleColumnFilterChange = useCallback((key: string, value: string) => {
        setColumnFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setCurrentPage(1); // Filtre değişince ilk sayfaya dön
    }, []);

    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setSelectedStatus('all');
        setColumnFilters({});
        setCurrentPage(1);
    }, []);

    // --- HESAPLANMIŞ VERİ (İstemci Tarafı Mantığı) ---

    const fallbackCachedRows = dataCacheKeyRef.current === pageDataCacheKey
        ? null
        : readPageDataCache<T>(pageDataCacheKey);
    const visibleData = dataCacheKeyRef.current === pageDataCacheKey
        ? data
        : (fallbackCachedRows ?? []);
    const visibleLoading = dataCacheKeyRef.current === pageDataCacheKey
        ? loading
        : fallbackCachedRows === null;

    const processedData = useMemo(() => {
        let result = [...visibleData];

        // 1. Filtreleme
        result = result.filter(item => {
            // Genel Arama
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const matchesSearch = stableSearchFields.some(field => {
                    const val = item[field];
                    return val && String(val).toLowerCase().includes(searchLower);
                });
                if (!matchesSearch) return false;
            }

            // Durum Filtresi
            if (selectedStatus !== 'all') {
                const isActive = (item as any).isActive; // isActive genelde standarttır
                if (selectedStatus === 'active' && !isActive) return false;
                if (selectedStatus === 'inactive' && isActive) return false;
            }

            // Sütun Filtreleri
            for (const [key, filterVal] of Object.entries(columnFilters)) {
                const normalizedFilter = String(filterVal ?? '').trim();
                if (!normalizedFilter) continue;
                if (normalizedFilter.toLowerCase() === 'all') continue;

                const itemVal = (item as any)[key];

                if (key === 'isActive') {
                    const desired = normalizedFilter.toLowerCase();
                    const itemIsActive = Boolean(itemVal);
                    if (desired === 'active' || desired === 'true' || desired === '1') {
                        if (!itemIsActive) return false;
                        continue;
                    }
                    if (desired === 'inactive' || desired === 'false' || desired === '0') {
                        if (itemIsActive) return false;
                        continue;
                    }
                }

                if (itemVal === undefined || itemVal === null) return false;
                if (typeof itemVal === 'object' && itemVal !== null) {
                    const maybeName = (itemVal as any).name;
                    if (typeof maybeName === 'string') {
                        if (!maybeName.toLowerCase().includes(normalizedFilter.toLowerCase())) return false;
                        continue;
                    }
                }

                if (!String(itemVal).toLowerCase().includes(normalizedFilter.toLowerCase())) return false;
            }

            return true;
        });

        // 2. Sıralama
        result.sort((a: any, b: any) => {
            const aVal = a[sortColumn];
            const bVal = b[sortColumn];

            if (aVal === bVal) return 0;
            if (aVal === undefined || aVal === null) return 1;
            if (bVal === undefined || bVal === null) return -1;

            const compare = aVal > bVal ? 1 : -1;
            return sortDirection === 'asc' ? compare : -compare;
        });

        return result;
    }, [visibleData, searchTerm, selectedStatus, columnFilters, sortColumn, sortDirection, stableSearchFields]);

    // 3. Sayfalama
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return processedData.slice(start, start + pageSize);
    }, [processedData, currentPage, pageSize]);

    const totalItems = processedData.length;
    const totalPages = Math.ceil(totalItems / pageSize);

    // Hook Arayüzü Döndür
    return {
        // Veri
        raw: visibleData,
        processed: processedData,
        data: paginatedData,
        loading: visibleLoading,
        error: formScope === mutationScope ? error : null,
        success: formScope === mutationScope ? success : null,

        // Durum (State)
        currentPage,
        pageSize,
        isPageSizeResolved: !pageSizePreferenceLoading,
        totalItems,
        totalPages,
        sortColumn,
        sortDirection,
        isSortManuallyChanged,
        searchTerm,
        selectedStatus,
        columnFilters,
        showModal: formScope === mutationScope && showModal,
        editingItem: formScope === mutationScope ? editingItem : null,
        prefillItem: formScope === mutationScope ? prefillItem : null,
        modalViewOnly: formScope === mutationScope && modalViewOnly,
        saving: formScope === mutationScope && saving,

        // Eylemler (Actions)
        setCurrentPage,
        setPageSize,
        setSearchTerm,
        setSelectedStatus,
        setColumnFilters,
        handleColumnFilterChange,
        handleSort,
        loadData,
        refresh,
        saveItem,
        updateItem,
        deleteItem,
        openModal,
        closeModal,
        clearFilters,
        setSuccess,
        setError
    };
}
